import Spinner from "components/Spinner";
import WoStatusOptions from "components/WoStatusOptions";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState, useCallback } from "react";
import { Accordion, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import ProductAccordion from "./ProductAccordion";
import { parsedJsonString } from "utils/UtilityFuncs";

export default function AddProductsToShipment({
  shipment,
  activeFilter,
  selectedCustomer,
  refreshProducts,
  refreshShipment,
  setActiveFilter,
}) {
  const dispatch = useDispatch();
  // const navigate = useNavigate();

  /*   All States
   ********************************************* */
  const [showModal, setShowModal] = useState(false);
  const [productsList, setProductsList] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shipmentQtyByProduct, setShipmentQtyByProduct] = useState({});
  const [expandedProducts, setExpandedProducts] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressOptions, setAddressOptions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [pendingProductsToAdd, setPendingProductsToAdd] = useState(null);

  const [loading, setLoading] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productLimit, setProductLimit] = useState(10);
  const [productTotal, setProductTotal] = useState(0);
  const [productPages, setProductPages] = useState(1);
  const [productQuery, setProductQuery] = useState("");

  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  /*   All Functions
   ********************************************* */
  // Helper to parse productSizes safely
  const parseSizes = (sizes) => {
    if (Array.isArray(sizes)) return sizes;
    if (typeof sizes === "string") {
      try {
        return JSON.parse(sizes);
      } catch {
        return [];
      }
    }
    return [];
  };

  // Helper to get product status label
  const getProductStatusLabel = (productId) => {
    const sizes = shipmentQtyByProduct[productId] || [];
    if (sizes.length === 0) return "Pending";
    const allShipped = sizes.every(
      (s) => s.shipmentQty >= s.quantity && s.quantity > 0
    );
    const someShipped = sizes.some((s) => s.shipmentQty > 0);
    if (allShipped) return "Fully Shipped";
    if (someShipped) return "Partially Shipped";
    return "Pending";
  };

  // Helper to check shipped status for color
  const getProductStatus = (productId) => {
    const sizes = shipmentQtyByProduct[productId] || [];
    if (sizes.length === 0) return "secondary";
    const allShipped = sizes.every((s) => s.shipmentQty >= s.quantity);
    const someShipped = sizes.some((s) => s.shipmentQty > 0);
    if (allShipped) return "success";
    if (someShipped) return "warning";
    return "secondary";
  };

  // Helper to compare two addresses
  const areAddressesEqual = (addr1, addr2) => {
    if (!addr1 || !addr2) return false;
    const a1 = typeof addr1 === "string" ? parsedJsonString(addr1) : addr1;
    const a2 = typeof addr2 === "string" ? parsedJsonString(addr2) : addr2;

    return JSON.stringify(a1) === JSON.stringify(a2);
  };

  // Check and get shipping address for products
  const getShippingAddressForProducts = (products) => {
    // Get shipment's current address
    const shipmentAddress = shipment?.shippingAddress
      ? parsedJsonString(shipment.shippingAddress)
      : null;

    // Get unique addresses from selected products with WO info
    const productAddressesWithWO = products
      .map((p) => ({
        address: p?.WorkOrder?.shippingAddress,
        woId: p?.WorkOrder?.woId,
        product: p,
      }))
      .filter((item) => item.address);

    const productAddresses = productAddressesWithWO.map((item) => item.address);

    // If no shipment address and no product addresses, return empty address without confirmation
    if (!shipmentAddress && productAddresses.length === 0) {
      return {
        address: null,
        needsConfirmation: false,
      };
    }

    // If shipment has no address, use first product's address without confirmation
    if (!shipmentAddress && productAddresses.length > 0) {
      return {
        address: productAddresses[0],
        needsConfirmation: false,
      };
    }

    // If shipment has address, check if all products have same address
    const uniqueAddressesWithWO = [];
    productAddressesWithWO.forEach((item) => {
      if (
        !uniqueAddressesWithWO.some((ua) =>
          areAddressesEqual(ua.address, item.address)
        )
      ) {
        uniqueAddressesWithWO.push(item);
      }
    });

    const uniqueAddresses = uniqueAddressesWithWO.map((item) => item.address);

    // If no product addresses, use shipment address
    if (uniqueAddresses.length === 0) {
      return {
        address: shipmentAddress,
        needsConfirmation: false,
      };
    }

    // Check if shipment address matches all product addresses
    const allMatchShipment = uniqueAddresses.every((addr) =>
      areAddressesEqual(shipmentAddress, addr)
    );

    if (allMatchShipment) {
      return {
        address: shipmentAddress,
        needsConfirmation: false,
      };
    }

    // If there are different addresses, need user confirmation (only when shipment has address)
    if (shipmentAddress && (uniqueAddresses.length > 1 || !allMatchShipment)) {
      const options = [
        { address: shipmentAddress, woId: null, isShipmentAddress: true },
        ...uniqueAddressesWithWO.map((item) => ({
          ...item,
          isShipmentAddress: false,
        })),
      ]
        .filter((opt) => opt.address)
        .filter(
          (opt, index, self) =>
            index ===
            self.findIndex((o) => areAddressesEqual(o.address, opt.address))
        );

      return {
        address: null,
        needsConfirmation: true,
        options: options,
      };
    }

    return {
      address: shipmentAddress || productAddresses[0],
      needsConfirmation: false,
    };
  };

  const addProductToShipment = async () => {
    // Check shipping address
    const addressResult = getShippingAddressForProducts(selectedProducts);

    if (addressResult.needsConfirmation) {
      // Show modal to select address
      setAddressOptions(addressResult.options);
      setPendingProductsToAdd({
        products: selectedProducts,
        type: "bulk",
      });
      setShowAddressModal(true);
      return;
    }

    // Proceed with adding products
    await executeAddProducts(selectedProducts, addressResult.address);
  };

  // filter out products with 0 total shipment qty
  const filterProductsToAdd = (products) => {
    return products.filter((prod) => {
      const sizes = shipmentQtyByProduct[prod.id] || [];
      const totalShipmentQty = sizes.reduce(
        (acc, s) => acc + Number(s.shipmentQty),
        0
      );
      return totalShipmentQty > 0;
    });
  };

  // Execute the actual API call to add products
  const executeAddProducts = async (productsToAdd, shippingAddress) => {
    productsToAdd = filterProductsToAdd(productsToAdd);
    if (productsToAdd.length === 0) {
      alert("Please specify shipment quantities for the selected products.");
      return;
    }

    // return;

    let address = shippingAddress;
    if (address && typeof address !== "string") {
      address = JSON.stringify(address);
    }

    let products = productsToAdd?.map((prod) => {
      const statusLabel = getProductStatusLabel(prod.id);
      let productStatus =
        statusLabel === "Fully Shipped"
          ? "shipped"
          : statusLabel === "Partially Shipped"
          ? "partially_shipped"
          : "pending";
      return {
        productId: prod.id,
        productSizes: JSON.stringify(shipmentQtyByProduct[prod.id] || []),
        shipmentId: shipment.shipmentId,
        woId: prod?.WorkOrder?.woId,
        productStatus,
      };
    });

    setLoading(true);

    try {
      const { data } = await API.post(
        "/shipment-product",
        { products, shippingAddress: address, shipmentId: shipment.shipmentId },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        await refreshProducts();
        await refreshShipment(shipment?.shipmentId);
        dispatch(
          setNotification({
            message: "Products added to shipment successfully",
            type: "success",
          })
        );
        setShowModal(false);
        setSelectedProducts([]);
        setShipmentQtyByProduct({});
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          // The API's reason, e.g. a size that would ship more than ordered.
          message: err?.response?.data?.message || err.message,
          type: "error",
        })
      );
    }
  };

  // Fetch products with pagination
  const getAllProducts = useCallback(
    async (opts = {}) => {
      try {
        loadingOn();
        const pageToUse = opts.page || productPage;
        const limitToUse = opts.limit || productLimit;
        const queryToUse = opts.query !== undefined ? opts.query : productQuery;
        let body = {
          status: activeFilter || "pending-processing",
          customerId: selectedCustomer?.id || undefined,
          page: pageToUse,
          limit: limitToUse,
          query: queryToUse,
          shipmentId: shipment?.shipmentId,
          searchFields: [
            "productType",
            "productStyle",
            "productColor",
            "productDescription",
          ],
        };
        const { data } = await API.post(
          "/sales/shipment/products-list-to-add",
          body,
          {
            headers: authHeader(),
          }
        );
        if (data?.success) {
          if (pageToUse === 1) {
            setProductsList(data?.workOrders || []);
          } else {
            setProductsList((prev) => [...prev, ...(data?.workOrders || [])]);
          }
          setProductTotal(data?.total || 0);
          setProductPages(data?.pages || 1);
          setProductPage(data?.page || pageToUse);
          setProductLimit(data?.limit || limitToUse);
        }
        loadingOff();
      } catch (err) {
        loadingOff();
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      }
    },
    [
      productPage,
      productLimit,
      productQuery,
      activeFilter,
      selectedCustomer,
      shipment?.shipmentId,
      dispatch,
    ]
  );

  // Load more products
  const handleLoadMoreProducts = () => {
    if (productPage < productPages) {
      getAllProducts({ page: productPage + 1 });
    }
  };

  // Change limit
  const handleLimitChange = (e) => {
    setProductLimit(Number(e.target.value));
    setProductPage(1);
    getAllProducts({ page: 1, limit: Number(e.target.value) });
  };

  // Add Single Product from list to shipment and refresh list without closing modal
  const handleDoneToAddProduct = async (product) => {
    // Check shipping address for this single product
    const addressResult = getShippingAddressForProducts([product]);

    if (addressResult.needsConfirmation) {
      // Show modal to select address
      setAddressOptions(addressResult.options);
      setPendingProductsToAdd({
        products: [product],
        type: "single",
      });
      setShowAddressModal(true);
      return;
    }

    // Proceed with adding product
    await executeSingleProductAdd(product, addressResult.address);
  };

  // Execute single product add
  const executeSingleProductAdd = async (product, shippingAddress) => {
    let address = shippingAddress;
    if (address && typeof address !== "string") {
      address = JSON.stringify(address);
    }

    let products = [product].map((prod) => {
      const statusLabel = getProductStatusLabel(prod.id);
      let productStatus =
        statusLabel === "Fully Shipped"
          ? "shipped"
          : statusLabel === "Partially Shipped"
          ? "partially_shipped"
          : "pending";
      return {
        productId: prod.id,
        productSizes: JSON.stringify(shipmentQtyByProduct[prod.id] || []),
        shipmentId: shipment.shipmentId,
        woId: prod?.WorkOrder?.woId,
        productStatus,
      };
    });

    // remove product from selectedProducts if exists
    setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));

    setLoading(true);

    try {
      const { data } = await API.post(
        "/shipment-product",
        { products, shippingAddress: address, shipmentId: shipment.shipmentId },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        await refreshProducts();
        await refreshShipment(shipment?.shipmentId);
        dispatch(
          setNotification({
            message: "Products added to shipment successfully",
            type: "success",
          })
        );
        // setShowModal(false);
        await getAllProducts({ page: 1 });
        // remove from selectedProducts
        setSelectedProducts((prev) => prev.filter((p) => p.id !== product.id));
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          // The API's reason, e.g. a size that would ship more than ordered.
          message: err?.response?.data?.message || err.message,
          type: "error",
        })
      );
    }
  };

  // Search products
  const handleProductSearch = (e) => {
    setProductQuery(e.target.value);
  };

  // reset products to initial state
  const resetProducts = () => {
    setProductQuery("");
    setProductPage(1);
    setSelectedProducts([]);
    setShipmentQtyByProduct({});
    getAllProducts({ page: 1, query: "" });
  };

  // Handle address selection confirmation
  const handleAddressConfirm = async () => {
    if (!selectedAddress || !pendingProductsToAdd) return;

    setShowAddressModal(false);

    if (pendingProductsToAdd.type === "single") {
      await executeSingleProductAdd(
        pendingProductsToAdd.products[0],
        selectedAddress
      );
    } else {
      await executeAddProducts(pendingProductsToAdd.products, selectedAddress);
    }

    // Reset
    setPendingProductsToAdd(null);
    setSelectedAddress(null);
    setAddressOptions([]);
  };

  // Format address for display
  const formatAddress = (address) => {
    const addr =
      typeof address === "string" ? parsedJsonString(address) : address;
    if (!addr) return "No Address";

    const parts = [
      addr.addressLine1,
      addr.addressLine2,
      addr.city,
      addr.state,
      addr.zip,
      addr.country,
    ].filter(Boolean);

    return parts.join(", ") || "No Address";
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (showModal) {
        getAllProducts({ page: 1 });
      }
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, productLimit, activeFilter, selectedCustomer]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (showModal && productQuery !== undefined) {
        setProductPage(1);
        getAllProducts({ page: 1, query: productQuery });
      }
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuery, showModal]);

  // When productsList changes, initialize shipment qty for each product/size to 0
  useEffect(() => {
    if (productsList.length > 0) {
      const initial = {};
      productsList.forEach((prod) => {
        const sizes = parseSizes(prod.productSizes);
        initial[prod.id] = sizes.map((size) => ({
          ...size,
          shipmentQty: 0,
        }));
      });
      setShipmentQtyByProduct(initial);
    }
  }, [productsList]);

  return (
    <div>
      <div>
        <button
          className="btn btn-sm btn-primary text-nowrap"
          onClick={() => setShowModal(true)}
        >
          Add Products
        </button>
      </div>

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedProducts([]);
          setShipmentQtyByProduct({});
        }}
        contentClassName="bg-light"
        size="xl"
        centered
      >
        <Modal.Header closeButton className="me-2">
          <Modal.Title className="w-100 d-flex justify-content-between align-items-center pe-2">
            <h4>Select Products to Add</h4>
            <div className="d-flex align-items-center gap-2">
              <label
                htmlFor="wo-status"
                className="form-label fs-5 mb-0 text-nowrap"
              >
                WO Status
              </label>
              <select
                name="wo-status"
                className="form-control"
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                }}
              >
                <option value="all">All</option>

                <option value="pending-processing">Pending + Processing</option>
                {/* Settings -> WO Statuses (2026-10-29) */}
                <WoStatusOptions />
              </select>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "78vh" }}>
          {loading && <Spinner />}
          <div className="mb-2 d-flex justify-content-between align-items-center gap-2">
            <div className="mb-2 d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-1">
                <input
                  className="form-control form-control-sm"
                  type="text"
                  placeholder="Search products..."
                  value={productQuery}
                  onChange={handleProductSearch}
                  style={{ maxWidth: 220 }}
                />
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => {
                    setProductQuery("");
                    setProductPage(1);
                    getAllProducts({ page: 1 });
                  }}
                >
                  Clear
                </button>
              </div>

              <span>
                Page: {productPage} / {productPages}
              </span>
              <span>Total: {productTotal}</span>
              <span>Limit: </span>
              <select
                value={productLimit}
                className="form-select form-select-sm"
                onChange={handleLimitChange}
                style={{ width: 80 }}
              >
                {[5, 10, 20, 50].map((lim) => (
                  <option key={lim} value={lim}>
                    {lim}
                  </option>
                ))}
              </select>
              <span>
                Showing {productsList.length} of {productTotal} results
              </span>
            </div>
            <div className="pe-2">
              <button
                onClick={resetProducts}
                className="btn btn-outline-danger btn-sm"
              >
                Reset
              </button>
            </div>
          </div>
          <div style={{ height: "70vh", overflowY: "auto" }}>
            <Accordion activeKey={expandedProducts}>
              {productsList.length === 0 && (
                <div className="alert alert-info mt-3">
                  No products available for this WO.
                </div>
              )}
              {productsList.map((product) => {
                const sizes = shipmentQtyByProduct[product.id] || [];
                // Calculate summary
                const totalQty = sizes.reduce(
                  (acc, s) => acc + Number(s.quantity),
                  0
                );
                const shippedQty = sizes.reduce(
                  (acc, s) => acc + Number(s.shipmentQty),
                  0
                );
                const pendingQty = totalQty - shippedQty;

                return (
                  <ProductAccordion
                    key={product.id}
                    product={product}
                    sizes={sizes}
                    totalQty={totalQty}
                    shippedQty={shippedQty}
                    pendingQty={pendingQty}
                    getProductStatus={getProductStatus}
                    shipmentQtyByProduct={shipmentQtyByProduct}
                    setShipmentQtyByProduct={setShipmentQtyByProduct}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                    expandedProducts={expandedProducts}
                    setExpandedProducts={setExpandedProducts}
                    handleDoneToAddProduct={handleDoneToAddProduct}
                    productLoading={loading}
                  />
                );
              })}
            </Accordion>
            {productPage < productPages && (
              <div className="p-2 text-center bg-light">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleLoadMoreProducts}
                  disabled={loading}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowModal(false);
            }}
          >
            Close
          </button>
          <button
            className="btn btn-primary"
            onClick={addProductToShipment}
            disabled={selectedProducts.length === 0 || loading === true}
          >
            Add
          </button>
        </Modal.Footer>
      </Modal>

      {/* Address Selection Modal */}
      <Modal
        show={showAddressModal}
        onHide={() => {
          setShowAddressModal(false);
          setPendingProductsToAdd(null);
          setSelectedAddress(null);
          setAddressOptions([]);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Shipping Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Multiple shipping addresses detected. Please select which address to
            use for this shipment:
          </p>
          <div className="d-flex flex-column gap-2">
            {addressOptions.map((option, index) => {
              const optionString = JSON.stringify(option.address);
              const isShipmentAddr =
                option.isShipmentAddress ||
                areAddressesEqual(option.address, shipment?.shippingAddress);
              return (
                <div
                  key={index}
                  className={`border rounded p-3 cursor-pointer ${
                    selectedAddress &&
                    JSON.stringify(selectedAddress) === optionString
                      ? "border-primary border-2 bg-light"
                      : "border-secondary"
                  }`}
                  onClick={() => setSelectedAddress(option.address)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-start">
                    <input
                      type="radio"
                      className="form-check-input mt-1 me-2"
                      checked={
                        selectedAddress &&
                        JSON.stringify(selectedAddress) === optionString
                      }
                      onChange={() => setSelectedAddress(option.address)}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>
                          {isShipmentAddr
                            ? "Current Shipment Address"
                            : option.woId
                            ? `Work Order #${option.woId}`
                            : `Product Address ${index + 1}`}
                        </strong>
                        {isShipmentAddr && (
                          <span className="badge bg-info">Current</span>
                        )}
                        {!isShipmentAddr && option.woId && (
                          <span className="badge bg-secondary">
                            WO-{option.woId}
                          </span>
                        )}
                      </div>
                      <div className="text-muted">
                        {formatAddress(option.address)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowAddressModal(false);
              setPendingProductsToAdd(null);
              setSelectedAddress(null);
              setAddressOptions([]);
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddressConfirm}
            disabled={!selectedAddress}
          >
            Confirm & Add Products
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
